import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import { ScreenshotViewerHeader } from '@components/ScreenshotViewerHeader';
import { ScreenshotThumbnailStrip } from '@components/ScreenshotThumbnailStrip';
import { useSetSupportWidgetHidden } from '@/contexts/SupportWidget.context';
import {
  getScreenshotFullSrc,
  getScreenshotPreviewSrc,
} from '@/utils/screenshotPrivacy';
import styles from './Screenshots.module.scss';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

/**
 * Full-screen screenshot viewer: custom light-themed header + thumbnail
 * strip (own prev/next arrows, active-thumbnail badge) layered into
 * yet-another-react-lightbox's `render.controls` slot, with the library's
 * own zoom/counter/fullscreen and default main-image nav arrows untouched.
 */
export const ScreenshotsLightbox = ({
  open,
  onClose,
  allScreenshotsList,
  selectedScreenshotIndex,
  setSelectedScreenshotIndex,
  selectedTimezone,
  employeeName,
  teamName,
  lightboxKey,
}) => {
  const fullscreenRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const setSupportWidgetHidden = useSetSupportWidgetHidden();

  useEffect(() => {
    setSupportWidgetHidden(open);
    return () => setSupportWidgetHidden(false);
  }, [open, setSupportWidgetHidden]);

  const slides = useMemo(
    () =>
      allScreenshotsList.map((shot) => ({
        src: getScreenshotFullSrc(shot),
        thumbnail: getScreenshotPreviewSrc(shot),
      })),
    [allScreenshotsList]
  );

  const thumbnailItems = useMemo(
    () =>
      allScreenshotsList.map((shot, index) => ({
        id: `${shot.activityId || 'activity'}-${shot.screenIndex ?? index}`,
        previewSrc: getScreenshotPreviewSrc(shot),
        label: shot.timestamp
          ? moment(shot.timestamp).tz(selectedTimezone).format('hh:mm A')
          : '',
      })),
    [allScreenshotsList, selectedTimezone]
  );

  const activeShot = allScreenshotsList[selectedScreenshotIndex];
  const currentSrc = slides[selectedScreenshotIndex]?.src;

  const handleDownload = async () => {
    if (!currentSrc) return;
    try {
      const response = await fetch(currentSrc);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `screenshot-${activeShot?.dayLabel || 'screenshot'}-${
        selectedScreenshotIndex + 1
      }.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download screenshot', error);
    }
  };

  const handleToggleFullscreen = () => {
    if (isFullscreen) {
      fullscreenRef.current?.exit();
    } else {
      fullscreenRef.current?.enter();
    }
  };

  return (
    <Lightbox
      key={lightboxKey ?? 'screenshots-lightbox'}
      open={open}
      close={onClose}
      index={selectedScreenshotIndex}
      slides={slides}
      on={{
        view: ({ index }) => setSelectedScreenshotIndex(index),
        enterFullscreen: () => setIsFullscreen(true),
        exitFullscreen: () => setIsFullscreen(false),
      }}
      plugins={[Counter, Zoom, Fullscreen]}
      carousel={{ finite: true, spacing: 0, padding: 0 }}
      fullscreen={{ ref: fullscreenRef }}
      className={styles.lightboxLight}
      toolbar={{ buttons: [] }}
      render={{
        controls: () =>
          slides.length > 0 && (
            <>
              <ScreenshotViewerHeader
                title="Screenshot"
                dateLabel={activeShot?.dayLabel}
                timeLabel={
                  activeShot?.timestamp
                    ? moment(activeShot.timestamp)
                        .tz(selectedTimezone)
                        .format('hh:mm A')
                    : undefined
                }
                employeeName={employeeName}
                teamName={teamName}
                onDownload={handleDownload}
                imageHref={currentSrc}
                onToggleFullscreen={handleToggleFullscreen}
                isFullscreen={isFullscreen}
                onClose={onClose}
                className={styles.lightboxHeaderPinned}
              />
              <ScreenshotThumbnailStrip
                items={thumbnailItems}
                selectedIndex={selectedScreenshotIndex}
                onSelect={setSelectedScreenshotIndex}
                className={styles.lightboxStripPinned}
              />
            </>
          ),
      }}
    />
  );
};
